import { CHANNELS } from "../shared/channels";
import { ACHIEVEMENTS, AWARD_CATEGORIES, AwardCategory, AwardsSummary, MEDALS, MedalDef, MedalState, layersOfDirt, medalShelf } from "../shared/achievements";
import { call } from "./bridge";
import { awardsNow, remember } from "./awards_store";
import { locale, t } from "./i18n";
import { Placard, openBench, openInfo } from "./medal_bench";
import { calm, isSecret, medalDef, medalEl } from "./medal_art";

// the medal case: a wooden cabinet with glass in front, one shelf per area of chix, each medal hanging from a ribbon in a velvet nook.
// the same pieces draw the tiny medals on home and on the leaderboard

type Shelf = AwardCategory | "special";

// each shelf has its own velvet and ribbon colour
const SHELF_LOOK: Record<Shelf, [string, string]> = {
  notes: ["#3a0f1a", "#a7343f"], plan: ["#10243a", "#3f7fb5"], studio: ["#2a1038", "#8a58c4"], upskill: ["#0f2e22", "#3fae7a"],
  social: ["#3a1230", "#c4589a"], habit: ["#33220b", "#c4883f"], explore: ["#0f2b33", "#3fa0b5"], special: ["#1a1a22", "#9aa3b8"]
};
const SHELVES: Array<{ id: Shelf; label: string }> = [...AWARD_CATEGORIES.map((item) => ({ id: item.id as Shelf, label: item.label })), { id: "special", label: "Special" }];

// what a rubbing session has already cleaned, until the server's own answer arrives. the server only ever lowers dirt, so the lower of the two is right
const localDirt = new Map<string, number>();
export const shownDirt = (state: Pick<MedalState, "id" | "dirt">): number => Math.min(state.dirt, localDirt.get(state.id) ?? 1);

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
}

const plate = (text: string, extra = ""): HTMLElement => node("span", `m-brass${extra}`, text);

const cap = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

// which achievement a medal is for, what that takes and when: the achievement's own date when it has one, so a medal added later still says when it was really earned
export function placardFor(def: MedalDef, state?: Pick<MedalState, "earned" | "at">): Placard {
  const shelf = medalShelf(def);
  const area = SHELVES.find((item) => item.id === shelf)?.label || "";
  const achievement = def.achievement ? ACHIEVEMENTS.find((item) => item.id === def.achievement) : undefined;
  const unlockedAt = achievement ? awardsNow()?.achievements.find((item) => item.id === achievement.id)?.at : "";
  const stamp = state?.earned ? unlockedAt || state.at : "";
  const area2 = def.category ? SHELVES.find((item) => item.id === def.category)?.label || "" : "";
  return {
    meta: `${t(cap(def.metal))} · ${t(area)}`,
    by: achievement ? t(achievement.name) : def.category ? t("{n} {area} points", { n: def.at ?? 0, area: t(area2) }) : t(def.name),
    desc: achievement ? t(achievement.desc) : t(def.desc),
    date: stamp ? new Date(stamp).toLocaleDateString(locale(), { day: "numeric", month: "long", year: "numeric" }) : ""
  };
}

export function openWorkbench(def: MedalDef, state: MedalState, repaint: () => void, from?: HTMLElement): void {
  openBench({
    def,
    placard: placardFor(def, state),
    dirt: shownDirt(state),
    returnFocus: from,
    onSave: (dirt) => {
      localDirt.set(def.id, dirt);
      void call<AwardsSummary>(CHANNELS.awardsClean, def.id, dirt).then((next) => {
        localDirt.delete(def.id);
        remember(next);
      }).catch(() => undefined);
    },
    onClose: repaint
  });
}

function nook(def: MedalDef, state: MedalState | undefined, index: number, shelf: Shelf, repaint: () => void): HTMLElement {
  const earned = Boolean(state?.earned);
  const dirt = state ? shownDirt(state) : 1;
  const box = node("button", `m-nook${earned ? "" : " empty"}${earned && dirt <= 0.02 ? " glow" : ""}`);
  box.type = "button";
  box.style.setProperty("--velvet", SHELF_LOOK[shelf][0]);
  box.style.setProperty("--rib", SHELF_LOOK[shelf][1]);
  if (earned) box.setAttribute("aria-label", `${t(def.name)}, ${dirt <= 0.02 ? t("fully polished") : t("needs polish")}`);
  else box.setAttribute("aria-label", isSecret(def) ? t("a medal you have not found yet") : `${t("locked")}: ${t(def.name)}, ${t(def.desc)}`);
  const hang = node("div", "m-hang");
  hang.style.setProperty("--sw", `${5 + (index % 5)}s`);
  hang.style.animationDelay = `${-index * 0.7}s`;
  const medal = medalEl(def, { size: 72, layers: layersOfDirt(dirt), locked: !earned });
  medal.setAttribute("aria-hidden", "true");
  hang.append(node("i", "m-pin"), medal);
  box.append(hang, plate(earned ? t(def.name) : "· · ·", " small m-plate"));
  if (earned && state) box.addEventListener("click", () => openWorkbench(def, state, repaint, box));
  // a medal you do not have yet says how to earn it, unless it is a secret
  else if (!isSecret(def)) box.addEventListener("click", () => openInfo(def, placardFor(def, state), box));
  return box;
}

export function paintCase(host: HTMLElement, summary: AwardsSummary, repaint: () => void): void {
  const states = new Map(summary.medals.map((item) => [item.id, item]));
  const earned = summary.medals.filter((item) => item.earned).length;
  const head = node("div", "m-case-head");
  head.append(plate(t("The Collection")), plate(t("{n} of {total}", { n: earned, total: summary.medals.length })));
  const glass = node("div", "m-glass");
  let index = 0;
  for (const shelf of SHELVES) {
    const defs = MEDALS.filter((def) => medalShelf(def) === shelf.id);
    if (!defs.length) continue;
    const box = node("div", "m-shelf");
    const row = node("div", "m-row");
    for (const def of defs) row.append(nook(def, states.get(def.id), index++, shelf.id, repaint));
    const board = node("div", "m-board");
    board.append(plate(t(shelf.label), " small"));
    box.append(row, board);
    glass.append(box);
  }
  // the glass reflection is its own layer, wider than the glass and moved with a transform, so moving the mouse never restyles the medals
  const reflect = node("i", "m-reflect");
  glass.append(reflect);
  const wrap = node("div", "m-case");
  wrap.append(head, glass);
  let frame = 0;
  wrap.addEventListener("pointermove", (event) => {
    if (frame || event.pointerType === "touch" || calm()) return;
    const x = event.clientX;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const r = glass.getBoundingClientRect();
      const share = Math.min(1, Math.max(0, (x - r.left) / (r.width || 1)));
      reflect.style.transform = `translate3d(${(share * 80 - 40).toFixed(0)}px,0,0)`;
    });
  });
  host.replaceChildren(wrap);
}

// tiny ones: home shows four as buttons that open the case, the leaderboard a row of 20px medals
export function miniMedal(state: Pick<MedalState, "id" | "dirt">, size: number): HTMLElement | null {
  const def = medalDef(state.id);
  if (!def) return null;
  const el = medalEl(def, { size, layers: layersOfDirt(shownDirt(state)) });
  el.setAttribute("aria-hidden", "true");
  return el;
}
